import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import api, { setToken, clearAllTokens, setUser, getUser } from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null);
  const [posUser, setPosUser] = useState(null);
  const [sessionTimeout, setSessionTimeout] = useState(30);

  const adminLogin = useCallback(async (username, password) => {
    const res = await api.post('/auth/admin/login', { username, password });
    const { token, user, must_change_password } = res.data;
    setToken('admin', token);
    setUser('admin', user);
    setAdminUser(user);
    return { user, must_change_password };
  }, []);

  const adminLogout = useCallback(async () => {
    try { await api.post('/auth/logout'); }
    catch { toast.error('Logout request failed'); }
    setToken('admin', null);
    setUser('admin', null);
    setAdminUser(null);
  }, []);

  const posLogin = useCallback(async (pin) => {
    const res = await api.post('/auth/pos/login', { pin });
    const { session_token, user, session_timeout_minutes } = res.data;
    setToken('pos', session_token);
    setUser('pos', user);
    setPosUser(user);
    setSessionTimeout(session_timeout_minutes || 30);
    return user;
  }, []);

  const posLogout = useCallback(async () => {
    try { await api.post('/auth/logout'); }
    catch { toast.error('Logout request failed'); }
    setToken('pos', null);
    setUser('pos', null);
    setPosUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    setAdminUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, must_change_password: false };
      setUser('admin', updated);
      return updated;
    });
  }, []);

  const value = useMemo(() => ({
    adminUser, posUser, adminLogin, adminLogout, posLogin, posLogout,
    changePassword, sessionTimeout
  }), [adminUser, posUser, adminLogin, adminLogout, posLogin, posLogout,
       changePassword, sessionTimeout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
